import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Auto-reload on chunk load failures (stale deploy cache)
    // Uses timestamp so multiple deploys in one session each get a reload
    if (this.isChunkLoadError(error)) {
      const key = "chunk-reload-" + window.location.pathname;
      const lastReload = Number(sessionStorage.getItem(key) || "0");
      if (Date.now() - lastReload > 10_000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();
        return;
      }
    }

    this.setState({ errorInfo });
  }

  private isChunkLoadError(error: Error): boolean {
    return (
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed") ||
      error.name === "ChunkLoadError"
    );
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[200px] rounded-lg border border-danger/20 bg-danger-bg p-6">
          <h2 className="text-lg font-semibold text-danger">Something went wrong</h2>
          <p className="mt-2 text-sm text-danger">
            {this.state.error?.message || "An unexpected error occurred"}
          </p>
          {this.state.errorInfo && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm font-medium text-danger">
                Error details
              </summary>
              <pre className="mt-2 max-h-[200px] overflow-auto whitespace-pre-wrap rounded bg-danger-bg p-3 text-xs text-danger">
                {this.state.error?.stack}
                {"\n\nComponent Stack:\n"}
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
          <button
            onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
            className="mt-4 rounded-md bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-danger/90"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
